import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ShieldCheck, RefreshCw, Filter, Sparkles, CheckCircle2, X, FileText, Hash, BookOpen, ArrowRight, Inbox } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { NewBadge } from '@/components/NewBadge';

const ACTION_TONE = {
  auto_confirm: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
  manual_confirm: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  manual_match: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  reject_match: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
};

export default function BankMatchAuditLogPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');
  const [limit, setLimit] = useState(100);
  const [timelineMatchId, setTimelineMatchId] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [narrative, setNarrative] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);

  const fetchAudit = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/bank-matching/audit-log?limit=${limit}`);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load audit log');
    } finally { setLoading(false); }
  };

  const openTimeline = async (matchId) => {
    if (!matchId) return;
    setTimelineMatchId(matchId);
    setTimeline(null);
    setNarrative(null);
    setTimelineLoading(true);
    try {
      const r = await api.get(`/bank-matching/audit-log/${matchId}/timeline`);
      setTimeline(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load timeline');
      setTimelineMatchId(null);
    } finally { setTimelineLoading(false); }
  };

  const fetchNarrative = async (refresh = false) => {
    if (!timelineMatchId) return;
    setNarrativeLoading(true);
    try {
      const r = await api.get(`/bank-matching/audit-log/${timelineMatchId}/narrate${refresh ? '?refresh=true' : ''}`);
      setNarrative(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate narrative');
    } finally { setNarrativeLoading(false); }
  };

  useEffect(() => { fetchAudit(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [limit]);

  const filtered = actionFilter === 'all' ? items : items.filter(i => i.action === actionFilter);
  const counts = items.reduce((acc, i) => { acc[i.action] = (acc[i.action] || 0) + 1; return acc; }, {});

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="bank-match-audit-page">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
              <ShieldCheck size={20} className="text-violet-700 dark:text-violet-400" />
            </div>
            <div>
              <h1 className="text-2xl font-outfit font-bold flex items-center gap-2">
                Bank Match Audit Log
                <NewBadge since="2026-04-23" featureId="page-bank-match-audit-narrate" size="xs" label="Replay-as-Story" />
              </h1>
              <p className="text-xs text-muted-foreground">Every confirm / reject / manual-match action with actor + entity context.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <select className="h-8 px-2 text-xs rounded border border-border bg-background"
              value={limit} onChange={(e) => setLimit(Number(e.target.value))} data-testid="audit-limit">
              <option value={50}>Last 50</option>
              <option value={100}>Last 100</option>
              <option value={250}>Last 250</option>
              <option value={500}>Last 500</option>
            </select>
            <Button size="sm" variant="outline" onClick={fetchAudit} disabled={loading} data-testid="audit-refresh">
              <RefreshCw size={12} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
            </Button>
          </div>
        </div>

        {/* Action filter chips */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-outfit flex items-center gap-2"><Filter size={14} /> Filter by action</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2 pt-0">
            <button onClick={() => setActionFilter('all')}
              className={`px-2 py-1 rounded-full text-xs ${actionFilter === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
              data-testid="audit-filter-all">All ({items.length})</button>
            {Object.entries(counts).map(([k, v]) => (
              <button key={k} onClick={() => setActionFilter(k)}
                className={`px-2 py-1 rounded-full text-xs ${actionFilter === k ? 'bg-primary text-primary-foreground' : ACTION_TONE[k] || 'bg-muted'}`}
                data-testid={`audit-filter-${k}`}>{k.replace(/_/g, ' ')} ({v})</button>
            ))}
          </CardContent>
        </Card>

        {/* Audit table */}
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            {loading ? (
              <div className="p-8 text-center text-xs text-muted-foreground">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-10 text-center" data-testid="audit-empty">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                  <Inbox size={20} className="text-violet-600 dark:text-violet-400" />
                </div>
                <h3 className="text-sm font-semibold mb-1">
                  {actionFilter !== 'all' ? `No "${actionFilter.replace(/_/g, ' ')}" entries yet` : 'No audit entries yet'}
                </h3>
                <p className="text-xs text-muted-foreground max-w-sm mx-auto mb-4">
                  This log fills up automatically once you start confirming, rejecting, or manually matching bank-statement transactions.
                  Upload a statement to kick off the smart-match engine.
                </p>
                <Button
                  size="sm"
                  className="bg-violet-600 hover:bg-violet-700"
                  onClick={() => navigate('/bank-statements')}
                  data-testid="audit-empty-cta"
                >
                  Go to Bank Statements <ArrowRight size={12} className="ml-1.5" />
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]" data-testid="audit-table">
                  <thead className="bg-muted/50 border-b border-border text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="text-left px-4 py-2">Timestamp</th>
                      <th className="text-left px-4 py-2">Action</th>
                      <th className="text-left px-4 py-2">Actor</th>
                      <th className="text-left px-4 py-2">Entity</th>
                      <th className="text-right px-4 py-2">Amount</th>
                      <th className="text-left px-4 py-2 hidden md:table-cell">Match ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(row => {
                      const d = row.details || {};
                      const ent = d.entity || {};
                      const matchId = d.match_id;
                      return (
                        <tr key={row.id}
                          className={`border-b border-border ${matchId ? 'cursor-pointer hover:bg-muted/40' : ''}`}
                          onClick={() => matchId && openTimeline(matchId)}
                          data-testid={`audit-row-${row.id}`}>
                          <td className="px-4 py-2 font-mono text-xs">{new Date(row.timestamp).toLocaleString()}</td>
                          <td className="px-4 py-2">
                            <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${ACTION_TONE[row.action] || 'bg-muted'}`}>
                              {(row.action || '').replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-xs truncate max-w-[180px]" title={row.user_email}>{row.user_email || row.user_id}</td>
                          <td className="px-4 py-2 text-xs">
                            {ent.name ? (
                              <>
                                <span className="text-[9px] uppercase font-bold mr-1 px-1 py-0.5 rounded bg-muted">{ent.entity_type}</span>
                                {ent.name}
                              </>
                            ) : '—'}
                          </td>
                          <td className="px-4 py-2 text-right font-mono text-xs">
                            {d.txn_amount ? Number(d.txn_amount).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '—'}
                          </td>
                          <td className="px-4 py-2 text-[10px] font-mono text-muted-foreground hidden md:table-cell truncate max-w-[140px]" title={matchId}>
                            {matchId ? (
                              <span className="inline-flex items-center gap-1 text-cyan-700 dark:text-cyan-400 font-bold">
                                <Hash size={10} />{matchId.slice(0, 8)}…
                              </span>
                            ) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Timeline drawer dialog */}
      <Dialog open={!!timelineMatchId} onOpenChange={(o) => { if (!o) { setTimelineMatchId(null); setTimeline(null); setNarrative(null); } }}>
        <DialogContent className="max-w-2xl max-h-[85dvh] overflow-y-auto" data-testid="audit-timeline-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-outfit">
              <Hash size={16} className="text-cyan-600" />
              Match Timeline
              <span className="text-[10px] font-mono text-muted-foreground">{timelineMatchId?.slice(0, 12)}…</span>
            </DialogTitle>
            {timeline?.entity_hint && (
              <DialogDescription className="flex items-center gap-2">
                <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-muted">{timeline.entity_hint.entity_type}</span>
                <strong>{timeline.entity_hint.name}</strong>
                <span className="text-xs text-muted-foreground">· confidence: {timeline.entity_hint.confidence}</span>
              </DialogDescription>
            )}
          </DialogHeader>

          {timelineLoading ? (
            <div className="p-8 text-center text-xs text-muted-foreground">Loading timeline…</div>
          ) : !timeline ? (
            <div className="p-8 text-center text-xs text-muted-foreground">No data.</div>
          ) : (
            <div className="space-y-3" data-testid="timeline-events">
              {/* Statement header */}
              {timeline.statement && (
                <div className="rounded-lg border border-border p-2 text-xs bg-muted/30">
                  <div className="flex items-center gap-1.5 text-muted-foreground"><FileText size={11} /> Source statement</div>
                  <div className="font-medium mt-0.5 truncate">{timeline.statement.file_name}</div>
                  {timeline.statement.period && <div className="text-[10px] text-muted-foreground">Period: {timeline.statement.period}</div>}
                </div>
              )}

              {/* Match summary */}
              {timeline.match && (
                <div className="rounded-lg border border-cyan-200 dark:border-cyan-900 p-3 text-xs bg-cyan-50/50 dark:bg-cyan-900/10">
                  <div className="flex items-center justify-between mb-1">
                    <strong className="text-cyan-800 dark:text-cyan-300">Auto-match record</strong>
                    <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${
                      timeline.match.auto_confirmed ? 'bg-violet-100 text-violet-700' :
                      timeline.match.status === 'confirmed' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>{timeline.match.auto_confirmed ? 'Auto-confirmed' : timeline.match.status}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <div className="text-[10px] text-muted-foreground uppercase font-bold">Bank txn</div>
                      <div className="font-mono">{timeline.match.txn_date} · {Number(timeline.match.txn_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                      <div className="text-[10px] text-muted-foreground truncate">{timeline.match.txn_desc}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-muted-foreground uppercase font-bold">System record</div>
                      <div className="font-mono">{timeline.match.match_date} · {Number(timeline.match.match_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                      <div className="text-[10px] text-muted-foreground truncate">{timeline.match.match_type} · score {timeline.match.confidence}%</div>
                    </div>
                  </div>
                </div>
              )}

              {/* AI narrative ("Replay this match as a story") */}
              <div className="rounded-lg border border-violet-200 dark:border-violet-900 p-3 bg-violet-50/40 dark:bg-violet-900/10">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-1.5 text-violet-800 dark:text-violet-300">
                    <BookOpen size={12} />
                    <strong className="text-xs">Replay this match as a story</strong>
                    {narrative?.model && (
                      <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-violet-200 text-violet-800 dark:bg-violet-800 dark:text-violet-200">
                        {narrative.cached ? 'cached' : narrative.model}
                      </span>
                    )}
                  </div>
                  {narrative ? (
                    <Button size="sm" variant="ghost" onClick={() => fetchNarrative(true)} disabled={narrativeLoading} data-testid="narrate-refresh-btn" className="h-6 text-[10px]">
                      <RefreshCw size={10} className={`mr-1 ${narrativeLoading ? 'animate-spin' : ''}`} />Re-generate
                    </Button>
                  ) : (
                    <Button size="sm" variant="default" onClick={() => fetchNarrative(false)} disabled={narrativeLoading} data-testid="narrate-btn" className="h-6 text-[10px] bg-violet-600 hover:bg-violet-700">
                      <Sparkles size={10} className="mr-1" />{narrativeLoading ? 'Narrating…' : 'Generate'}
                    </Button>
                  )}
                </div>
                {narrative ? (
                  <p className="text-xs leading-relaxed text-foreground/90 italic" data-testid="narrate-text">
                    "{narrative.narrative}"
                  </p>
                ) : (
                  <p className="text-[11px] text-muted-foreground">Generate a plain-English story summarising every event in this match — perfect for audit reports.</p>
                )}
              </div>

              {/* Event timeline */}
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-2">Events ({timeline.event_count})</div>
                <ol className="relative border-l-2 border-border pl-4 space-y-3">
                  {timeline.events.map((e, i) => {
                    const isAuto = e.kind === 'auto_match';
                    const isReject = e.action === 'reject_match';
                    const isConfirm = e.action === 'manual_confirm' || e.action === 'auto_confirm' || e.action === 'confirmed';
                    const Icon = isAuto ? Sparkles : isReject ? X : isConfirm ? CheckCircle2 : ShieldCheck;
                    const tone = isAuto ? 'bg-cyan-500' : isReject ? 'bg-rose-500' : isConfirm ? 'bg-emerald-500' : 'bg-violet-500';
                    return (
                      <li key={i} className="relative" data-testid={`timeline-event-${i}`}>
                        <span className={`absolute -left-[22px] top-1 w-4 h-4 rounded-full ${tone} flex items-center justify-center ring-4 ring-background`}>
                          <Icon size={8} className="text-white" />
                        </span>
                        <div className="text-xs">
                          <div className="flex items-center justify-between gap-2">
                            <strong className="capitalize">{(e.action || '').replace(/_/g, ' ')}</strong>
                            <span className="text-[10px] font-mono text-muted-foreground">{e.timestamp ? new Date(e.timestamp).toLocaleString() : ''}</span>
                          </div>
                          <div className="text-[11px] text-muted-foreground">by <span className="font-medium text-foreground">{e.actor}</span></div>
                          {e.details?.confidence !== undefined && e.kind === 'auto_match' && (
                            <div className="text-[10px] text-muted-foreground mt-0.5">score: {e.details.confidence}%</div>
                          )}
                          {e.details?.entity?.name && e.kind === 'audit' && (
                            <div className="text-[10px] text-muted-foreground mt-0.5">entity: {e.details.entity.entity_type} · {e.details.entity.name}</div>
                          )}
                          {e.details?.auto && e.kind === 'decision' && (
                            <div className="text-[10px] text-violet-600 mt-0.5">✨ auto-confirmed (high-confidence + IBAN exact)</div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
